// Added by Sejune Oh on 2020-03-30
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC } from 'react'
import { createStyles, Theme, makeStyles, useTheme } from '@material-ui/core'
import { NavLink } from 'react-router-dom'
import cn from 'classnames'

// UI
import { Container } from '@/components/_primes/v2/Container'
import { SearchInput } from '@/components/common/v2'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface ManagementLayoutProps {
  /** Bread crumbs */
  breadCrumbs: ManagementBreadCrumbProps[]
  /** Top bar의 message */
  message?: string
  /** Page name */
  page: string
  /** Keyword Search */
  onSearch?: (keyword: string) => void
  /** Search input key Up evt handler */
  onSearchKeyUp?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** layout full width */
  fullWidth?: boolean
  children: any
}

export interface ManagementBreadCrumbProps {
  name: string
  link: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Management page의 기본 레이아웃 입니다.
 */
const ManagementLayout: FC<ManagementLayoutProps> = ({
  page,
  breadCrumbs,
  message,
  onSearch: handleSearch,
  onSearchKeyUp: handleSearchKeyUp,
  fullWidth = false,
  children,
  ...props
}) => {
  // ! Style
  const classes = useStyles(useTheme())

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <div className={classes.wrapper}>
      <div className={classes.topbar}>
        {/* ---------- Bread Crumbs ---------- */}
        <div className={classes.breadCrumbWrapper}>
          {breadCrumbs.map(({ name, link }, index) => (
            <NavLink
              key={`link-${name}`}
              className={cn(classes.link, { [classes.currentLink]: index + 1 === breadCrumbs.length })}
              to={link}
            >
              {name}
              {index + 1 !== breadCrumbs.length ? ' > ' : ''}
            </NavLink>
          ))}
        </div>
        {/* ---------- Bread Crumbs ---------- */}

        {/* ---------- Topbar Message ---------- */}
        {message && <div className={classes.message}>{message}</div>}
        {/* ---------- Topbar Message ---------- */}

        {/* ----------  Search ---------- */}
        <div className={classes.searchWrapper}>{handleSearch && <SearchInput onSearch={handleSearch} onKeyUp={handleSearchKeyUp} />}</div>
        {/* ----------  Search ---------- */}
      </div>

      {/* ----------  Contents ---------- */}
      <Container maxWidth={'xl'} className={fullWidth ? classes.contentWrapperFullWidth : classes.contentWrapper}>
        {children}
      </Container>
      {/* ----------  Contents ---------- */}
    </div>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      maxWidth: '990px',
      width: '100%',
    },
    topbar: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: `${theme.spacing()}px 0`,
      minHeight: '60px',
      alignItems: 'center',
      borderBottom: '1px solid rgb(99, 99, 99)',
      backgroundColor: '#303030',
      position: 'fixed',
      maxWidth: '990px',
      width: '100%',
      zIndex: 2
    },
    breadCrumbWrapper: {},
    message: {
      color: 'rgba(255, 255, 255, 0.4)',
      fontSize: '12px',
      display: 'flex',
      flex: 1,
      justifyContent: 'flex-end',
      alignSelf: 'flex-end',
      padding: `0 ${theme.spacing()}px`,
    },
    link: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.7)',
      textDecoration: 'unset',
    },
    currentLink: {
      color: '#fff',
    },
    searchWrapper: {},
    contentWrapper: {
      padding: `60px ${theme.spacing()}px 0 ${theme.spacing()}px`,
    },
    contentWrapperFullWidth: {
      padding: '60px 0'
    }
  })
)

export default ManagementLayout
